import {
  Modal,
  ModalProvider,
  PrimaryButton,
  TextField,
} from '@joor/design-system'
import { useFormik } from 'formik'
import styled from 'styled-components/macro'
import * as yup from 'yup'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'

import { useUpdateOrderContacts } from '../ContactsWidget/contactsList.mutations'
import { useOrderId } from 'features/Orders/orders.hooks'

export enum AddBuyerModalTestIds {
  Modal = 'add-buyer-modal-container',
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onAddBuyer?: () => void
}

export const AddBuyerModal = ({ isOpen, onClose, onAddBuyer }: Props) => {
  const Portal = usePortal()

  const { renderSuccessToast, renderErrorToast } = useRenderToasters()

  const orderId = useOrderId()
  const { onUpdate } = useUpdateOrderContacts(orderId)

  const handleAddBuyer = () => {
    onUpdate({
      orderIds: [orderId],
      orderDetails: {
        buyerName: form.values.name,
        buyerEmail: form.values.email,
      },
    })
      .then(() => {
        onAddBuyer?.()
        renderSuccessToast({
          title: 'Success',
          description: 'Buyer added successfully.',
        })
      })
      .catch(() => {
        renderErrorToast({
          title: 'Error',
          description: 'The buyer could not be added.',
        })
      })

    onClose()
  }

  const form = useFormik<yup.InferType<typeof addBuyerFormSchema>>({
    validationSchema: addBuyerFormSchema,
    initialValues: { name: '', email: '' },
    onSubmit: handleAddBuyer,
    initialErrors: {
      name: 'Name cannot be empty',
      email: 'Email cannot be empty',
    },
  })

  return (
    <Portal>
      <ModalProvider>
        <StyledModal
          testId={AddBuyerModalTestIds.Modal}
          isOpen={isOpen}
          onClose={onClose}
          title={{
            text: 'Add new buyer',
          }}
          size="small"
        >
          <ContentWrapper>
            <TextField
              id="name"
              label={'Buyer Name'}
              value={form.values.name}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              errorMessage={form.touched.name ? form.errors.name : ''}
            />
            <TextField
              id="email"
              label={'Buyer Email'}
              value={form.values.email}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              errorMessage={form.touched.email ? form.errors.email : ''}
            />
          </ContentWrapper>

          <StyledModalActions>
            <PrimaryButton
              type="submit"
              onClick={form.submitForm}
              disabled={!form.isValid}
            >
              Add
            </PrimaryButton>
          </StyledModalActions>
        </StyledModal>
      </ModalProvider>
    </Portal>
  )
}

const addBuyerFormSchema = yup.object({
  name: yup.string().required('Name cannot be empty'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email cannot be empty'),
})

const StyledModal = styled(Modal)`
  & > div:nth-child(2) {
    overflow: visible;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 8px;
`

const StyledModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  order: 3;
  padding: 16px 24px 0 16px;
  margin-top: 10px;
  border-top: 1px solid var(--color-primary-200);
`
